/**
 * APIClient - Voice Chat API Communication
 * Communicates with STT server and main Chat API
 */
class APIClient {
    constructor(config = {}) {
        this.baseURL = config.baseURL || "https://kbot-dev.systemevererp.com";
        this.timeout = config.timeout || 30000;
        this.tokenData = config.tokenData
            ? JSON.parse(config.tokenData)
            : undefined;
    }
    /**
     * Method 1: STT -> Text -> Main API
     */
    async sendVoiceWithSTT(audioBlob, options) {
        try {
            // Step 0: generate token
            console.log("[APIClient] Generating token...");
            const token = await this.generateToken();
            //Step 0-1: request token
            console.log("[APIClient] Requesting token...");
            const kbotToken = await this.requestToken(token);
            // Step 1: Send audio to STT server -> Convert to text
            console.log("[APIClient] Calling STT API...");
            const text = await this.convertSpeechToText(audioBlob, kbotToken);
            console.log("[APIClient] STT conversion complete:", text);
            // Step 2: Send converted text to main API
            console.log("[APIClient] Calling Chat API...");
            const response = options?.onText
                ? await options.onText(text)
                : await this.sendTextToChat(text);
            return response;
        }
        catch (err) {
            console.error("[APIClient] STT method error:", err);
            throw err;
        }
    }
    /**
     * Method 2: Direct voice binary transmission
     */
    async sendVoiceDirect(audioBlob) {
        try {
            const formData = new FormData();
            formData.append("audio", audioBlob, "voice.webm");
            formData.append("format", audioBlob.type);
            console.log("[APIClient] Calling Voice Chat API directly...");
            const response = await fetch(`${this.baseURL}/chat`, {
                method: "POST",
                body: formData,
                headers: {
                    Accept: "application/json",
                },
                signal: AbortSignal.timeout(this.timeout),
            });
            if (!response.ok) {
                throw new Error(`API Error: ${response.status} ${response.statusText}`);
            }
            const data = await response.json();
            console.log("[APIClient] Voice Chat API response received");
            return data;
        }
        catch (err) {
            console.error("[APIClient] Direct transmission error:", err);
            throw err;
        }
    }
    /**
     * STT API call
     */
    async convertSpeechToText(audioBlob, token) {
        const formData = new FormData();
        const mimeType = this.getAudioMimeType(audioBlob.type);
        const extension = this.getFileExtension(mimeType);
        const blob = new Blob([audioBlob], { type: mimeType });
        formData.append("file", blob, `voice.${extension}`);
        const headers = {};
        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        }
        const response = await fetch(`${this.baseURL}/api/v1/audio/transcriptions`, {
            method: "POST",
            headers,
            body: formData,
            signal: AbortSignal.timeout(this.timeout),
        });
        if (!response.ok) {
            const errorBody = await response.text();
            throw new Error(`STT Error: ${response.status} ${response.statusText} - ${errorBody}`);
        }
        const data = await response.json();
        if (!data.text) {
            throw new Error("STT response contains no text");
        }
        return data.text;
    }
    // async convertSpeechToText(audioBlob: Blob): Promise<string> {
    //   const formData = new FormData();
    //   formData.append('audio', audioBlob, 'voice.webm');
    //   formData.append('language', 'ko-KR');
    //   formData.append('format', audioBlob.type);
    //
    //   try {
    //     const response = await fetch(`${this.baseURL}/stt`, {
    //       method: 'POST',
    //       body: formData,
    //       signal: AbortSignal.timeout(this.timeout)
    //     });
    //
    //     if (!response.ok) {
    //       throw new Error(`STT Error: ${response.status} ${response.statusText}`);
    //     }
    //
    //     const data: STTResponse = await response.json();
    //
    //     if (!data.text) {
    //       throw new Error('STT response contains no text');
    //     }
    //
    //     return data.text;
    //   } catch (err) {
    //     console.warn('[APIClient] STT fallback used:', err);
    //     return 'Hello, this is Flextudio.';
    //   }
    // }
    getAudioMimeType(blobType) {
        const supportedTypes = [
            "audio/mpeg",
            "audio/wav",
            "audio/ogg",
            "audio/x-m4a",
        ];
        if (supportedTypes.includes(blobType)) {
            return blobType;
        }
        // Default to m4a for unsupported types (e.g., audio/webm)
        return "audio/x-m4a";
    }
    getFileExtension(mimeType) {
        const extensionMap = {
            "audio/mpeg": "mp3",
            "audio/wav": "wav",
            "audio/ogg": "ogg",
            "audio/x-m4a": "m4a",
        };
        return extensionMap[mimeType] || "m4a";
    }
    /**
     * Main Chat API call (text)
     */
    async sendTextToChat(text) {
        const response = await fetch(`${this.baseURL}/chat`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                message: text,
                timestamp: Date.now(),
            }),
            signal: AbortSignal.timeout(this.timeout),
        });
        if (!response.ok) {
            throw new Error(`Chat API Error: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        return data;
    }
    /**
     * Streaming method (optional)
     */
    async sendVoiceStreaming(audioBlob, onProgress) {
        const chunkSize = 8192; // 8KB chunks
        const arrayBuffer = await audioBlob.arrayBuffer();
        const totalChunks = Math.ceil(arrayBuffer.byteLength / chunkSize);
        console.log(`[APIClient] Starting streaming: ${totalChunks} chunks`);
        for (let i = 0; i < totalChunks; i++) {
            const start = i * chunkSize;
            const end = Math.min(start + chunkSize, arrayBuffer.byteLength);
            const chunk = arrayBuffer.slice(start, end);
            await this.sendChunk(chunk, i, totalChunks);
            if (onProgress) {
                onProgress((i + 1) / totalChunks);
            }
        }
        console.log("[APIClient] Streaming complete");
    }
    async sendChunk(chunk, index, total) {
        const response = await fetch(`${this.baseURL}/stream`, {
            method: "POST",
            headers: {
                "Content-Type": "application/octet-stream",
                "X-Chunk-Index": index.toString(),
                "X-Total-Chunks": total.toString(),
            },
            body: chunk,
            signal: AbortSignal.timeout(this.timeout),
        });
        if (!response.ok) {
            throw new Error(`Chunk transmission failed: ${response.status}`);
        }
        return await response.json();
    }
    /**
     * Set API base URL
     */
    setBaseURL(url) {
        this.baseURL = url;
        console.log("[APIClient] Base URL changed:", url);
    }
    /**
     * Set timeout
     */
    setTimeout(timeout) {
        this.timeout = timeout;
        console.log("[APIClient] Timeout changed:", timeout);
    }
    /**
     * Get current base URL
     */
    getBaseURL() {
        return this.baseURL;
    }
    /**
     * Get current timeout
     */
    getTimeout() {
        return this.timeout;
    }
    async generateToken() {
        const d = this.tokenData;
        const body = JSON.stringify({
            email: String(d.email ?? ""),
            companySeq: String(d.companySeq ?? "0"),
            deptSeq: String(d.deptSeq ?? ""),
            dsnBis: String(d.dsnBis ?? ""),
            dsnOper: String(d.dsnOper ?? ""),
            empSeq: String(d.empSeq ?? ""),
            userSeq: String(d.userSeq ?? "0"),
            languageSeq: String(d.languageSeq ?? "0"),
            dm_url: String(d.dm_url ?? ""),
            erpDomain: String(d.erpDomain ?? ""),
        });
        const res = await fetch("https://service.flextudio.com/ksystem/kbotToken", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
        });
        if (!res.ok) {
            const text = await res.text();
            throw new Error(`Error code ${res.status}: ${text}`);
        }
        const data = await res.json();
        if (!data.token) {
            throw new Error("kbotGetToken: token not found in response");
        }
        return data.token;
    }
    async requestToken(token) {
        const response = await fetch(`${this.baseURL}/api/v1/auths/external/ex_signin`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ext_token: token,
                duration: "1m",
            }),
        });
        if (!response.ok) {
            const errorData = await response.json();
            console.error("Error response:", errorData);
            throw new Error(`Token request failed: ${response.status}`);
        }
        const jsonValue = await response.json();
        if (!jsonValue.token) {
            throw new Error("Token not found in response");
        }
        return jsonValue.token;
    }
}

/**
 * IOSBridge - iOS Native Bridge Communication
 * Communicates with native iOS code via window.webkit.messageHandlers.MobileTest
 */
class IOSBridge {
    constructor() {
        window['bridgeCallback'] = (funcName, err, result) => {
            console.log(`[IOSBridge] bridgeCallback: ${funcName}`, JSON.stringify({ err, result }));
            const cb = window[funcName];
            if (typeof cb === 'function') {
                cb(err, result);
            }
            else {
                console.warn(`[IOSBridge] No callback registered for: ${funcName}`);
            }
        };
    }
    async initialize() {
        return new Promise((resolve, reject) => {
            this.sendAction('VoiceInit', {}, (err, result) => {
                if (this.isErr(err)) {
                    reject(new Error(String(result ?? err)));
                    return;
                }
                console.log('[IOSBridge] VoiceInit result:', JSON.stringify(result));
                resolve({ success: true, result: result });
            });
        });
    }
    async startRecording() {
        return new Promise((resolve, reject) => {
            this.sendAction('StartRecording', {}, (err, result) => {
                if (this.isErr(err)) {
                    reject(new Error(String(result ?? err)));
                    return;
                }
                console.log('[IOSBridge] StartRecording result:', JSON.stringify(result));
                resolve({ success: true, result: result });
            });
        });
    }
    async stopRecording() {
        return new Promise((resolve, reject) => {
            this.sendAction('StopRecording', {}, (err, result) => {
                if (this.isErr(err)) {
                    reject(new Error(String(result ?? err)));
                    return;
                }
                console.log('[IOSBridge] StopRecording result:', JSON.stringify(result));
                // Extract audio data if present
                let parsed = {};
                if (typeof result === 'string') {
                    try {
                        parsed = JSON.parse(result);
                    }
                    catch { /* use empty */ }
                }
                else if (result && typeof result === 'object') {
                    parsed = result;
                }
                resolve({
                    success: true,
                    audioData: typeof parsed['0'] === 'string' ? parsed['0'] : undefined,
                    result: parsed,
                });
            });
        });
    }
    async checkPermission() {
        return new Promise((resolve, reject) => {
            this.sendAction('CheckVoicePermission', {}, (err, result) => {
                if (this.isErr(err)) {
                    reject(new Error(String(result ?? err)));
                    return;
                }
                console.log('[IOSBridge] CheckVoicePermission result:', JSON.stringify(result));
                resolve({ success: true, result: result });
            });
        });
    }
    /** err may arrive as boolean true/false or the strings "true"/"false" */
    isErr(err) {
        if (err === 'false' || err === false || err === null || err === undefined)
            return false;
        return Boolean(err);
    }
    /**
     * Send an action to native and call back with (err, result).
     * Handles unique name generation, timeout, and cleanup.
     */
    sendAction(action, param, callback) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mobileTest = window.webkit?.messageHandlers?.MobileTest;
        if (!mobileTest) {
            callback(new Error('iOS bridge not available'), null);
            return;
        }
        let callbackName = `${action}_${Date.now()}`;
        while (window[callbackName]) {
            callbackName = `${action}_${Date.now()}`;
        }
        const timer = setTimeout(() => {
            delete window[callbackName];
            console.warn(`[IOSBridge] Timeout: ${action}`);
            callback(new Error('Native call timeout'), null);
        }, 30000);
        window[callbackName] = (err, result) => {
            console.log(`[IOSBridge] window[${callbackName}] called:`, JSON.stringify({ err, result }));
            clearTimeout(timer);
            delete window[callbackName];
            callback(err, result);
        };
        try {
            mobileTest.postMessage(JSON.stringify({
                action,
                param,
                callback: {
                    function: 'bridgeCallback',
                    runFunction: callbackName,
                },
            }));
            console.log(`[IOSBridge] Native call: ${action}`);
        }
        catch (err) {
            clearTimeout(timer);
            delete window[callbackName];
            console.error('[IOSBridge] Call error:', err);
            callback(new Error('Failed to call iOS bridge'), null);
        }
    }
    dispose() {
        console.log('[IOSBridge] Disposed');
    }
}

/**
 * AndroidBridge - Android Native Bridge Communication
 * Communicates with native Android code through window.AndroidVoiceBridge interface
 */
class AndroidBridge {
    constructor() {
        this.callbacks = new Map();
        this.callbackId = 0;
        // Register global callback for Android native responses
        window.onAndroidVoiceCallback = this.handleNativeCallback.bind(this);
    }
    async initialize() {
        return await this.call('voiceInit');
    }
    async startRecording() {
        return await this.call('StartRecording');
    }
    async stopRecording() {
        return await this.call('StopRecording');
    }
    async checkPermission() {
        return await this.call('voiceCheckPermission');
    }
    call(method, params = {}) {
        return new Promise((resolve, reject) => {
            const callbackId = this.callbackId++;
            this.callbacks.set(callbackId, { resolve, reject });
            // Check Android WebView interface
            if (window.AndroidVoiceBridge) {
                try {
                    const paramsJson = JSON.stringify(params);
                    window.AndroidVoiceBridge.call(method, paramsJson, callbackId);
                    console.log(`[AndroidBridge] Native call: ${method}, callbackId: ${callbackId}`);
                }
                catch (err) {
                    console.error('[AndroidBridge] Call error:', err);
                    this.callbacks.delete(callbackId);
                    reject(new Error('Failed to call Android bridge'));
                }
            }
            else {
                this.callbacks.delete(callbackId);
                reject(new Error('Android bridge not available'));
            }
            // Timeout after 30 seconds
            setTimeout(() => {
                if (this.callbacks.has(callbackId)) {
                    console.warn(`[AndroidBridge] Timeout: ${method}, callbackId: ${callbackId}`);
                    this.callbacks.delete(callbackId);
                    reject(new Error('Native call timeout'));
                }
            }, 30000);
        });
    }
    handleNativeCallback(callbackId, success, resultJson, error) {
        if (!this.callbacks.has(callbackId)) {
            return;
        }
        console.log(`[AndroidBridge] Native response: callbackId: ${callbackId}, success: ${success}`);
        const callback = this.callbacks.get(callbackId);
        this.callbacks.delete(callbackId);
        if (success) {
            try {
                const result = resultJson ? JSON.parse(resultJson) : { success: true };
                callback.resolve(result);
            }
            catch (err) {
                console.error('[AndroidBridge] Failed to parse response:', err);
                callback.reject(new Error('Invalid response from native'));
            }
        }
        else {
            callback.reject(new Error(error || 'Unknown error'));
        }
    }
    dispose() {
        this.callbacks.clear();
        window.onAndroidVoiceCallback = undefined;
        console.log('[AndroidBridge] Disposed');
    }
}

/**
 * PlatformDetector - Detect platform and WebView environment
 */
class PlatformDetector {
    /**
     * Detect current platform environment
     */
    static detect() {
        const ua = navigator.userAgent;
        let platform = 'Web';
        if (/iPhone|iPad|iPod/i.test(ua))
            platform = 'iOS';
        else if (/Android/i.test(ua))
            platform = 'Android';
        const isIOSWebView = PlatformDetector.getIOSRuntime() === 'ios-webview';
        const isAndroidWebView = platform === 'Android' && PlatformDetector.isRunningInWebView();
        const isWebView = isIOSWebView || isAndroidWebView;
        return {
            platform,
            isWebView,
            isIOS: platform === 'iOS',
            isAndroid: platform === 'Android',
            isIOSWebView,
            isAndroidWebView,
        };
    }
    /**
     * Check if running in a WebView
     */
    static isRunningInWebView() {
        const standalone = navigator.standalone;
        const userAgent = navigator.userAgent.toLowerCase();
        const safari = /safari/.test(userAgent);
        const ios = /iphone|ipod|ipad/.test(userAgent);
        const android = /android/.test(userAgent);
        // iOS WebView detection
        if (ios) {
            // WKWebView: standalone is undefined and safari is false
            if (standalone === undefined && !safari)
                return true;
        }
        // Android WebView detection
        if (android) {
            // Android WebView contains 'wv'
            if (userAgent.includes('wv'))
                return true;
        }
        return false;
    }
    static getIOSRuntime() {
        const ua = navigator.userAgent.toLowerCase();
        const isIOS = /iphone|ipad|ipod/.test(ua);
        if (!isIOS)
            return 'not-ios';
        const isStandalone = navigator.standalone === true;
        const hasSafari = /safari/.test(ua);
        const isChromeiOS = /crios/.test(ua);
        const isFirefoxiOS = /fxios/.test(ua);
        const isEdgeiOS = /edgios/.test(ua);
        const isKnownInApp = /kakaotalk|naver|instagram|fban|fbav|line|tiktok/.test(ua);
        if (isStandalone)
            return 'ios-pwa';
        if (isKnownInApp)
            return 'ios-inapp-browser';
        if (isChromeiOS)
            return 'ios-chrome';
        if (isFirefoxiOS)
            return 'ios-firefox';
        if (isEdgeiOS)
            return 'ios-edge';
        if (hasSafari)
            return 'ios-safari-or-safari-view';
        return 'ios-webview';
    }
}

/**
 * WebRecorder - Web API based audio recording
 * Uses MediaRecorder API for recording in browser environment
 */
class WebRecorder {
    constructor(environment) {
        this.recorder = null;
        this.audioStream = null;
        this.audioContext = null;
        this.audioChunks = [];
        this.isRecording = false;
        this.silenceFrame = null;
        this.analyserSource = null;
        this.environment = environment;
    }
    async initialize() {
        try {
            // Check for getUserMedia support
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error('getUserMedia not supported');
            }
            console.log('[WebRecorder] Initialized successfully (no permission requested)');
            return { success: true };
        }
        catch (err) {
            console.error('[WebRecorder] Initialization failed:', err);
            return {
                success: false,
                error: err instanceof Error ? err.message : 'Unknown error'
            };
        }
    }
    async startRecording(onDataAvailable) {
        if (this.isRecording) {
            console.warn('[WebRecorder] Already recording');
            return;
        }
        if (!this.audioStream) {
            await this.ensureAudioStream();
        }
        if (!this.audioStream) {
            throw new Error('Failed to initialize audio stream');
        }
        this.audioChunks = [];
        const options = this.getRecorderOptions();
        this.recorder = new MediaRecorder(this.audioStream, options);
        this.recorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                this.audioChunks.push(event.data);
                if (onDataAvailable) {
                    onDataAvailable(event.data);
                }
            }
        };
        this.recorder.start(100);
        this.isRecording = true;
        console.log('[WebRecorder] Recording started');
    }
    async ensureAudioStream() {
        try {
            // iOS requires AudioContext initialization
            if (this.environment.isIOS) {
                const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                if (AudioContextClass && !this.audioContext) {
                    this.audioContext = new AudioContextClass({
                        sampleRate: 16000,
                        latencyHint: 'interactive'
                    });
                }
                // iOS starts in suspended state - needs resume (user gesture)
                if (this.audioContext?.state === 'suspended') {
                    await this.audioContext.resume();
                }
            }
            this.audioStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 16000,
                    channelCount: 1
                }
            });
        }
        catch (err) {
            console.error('[WebRecorder] Failed to acquire microphone stream:', err);
            throw err;
        }
    }
    /**
     * Start monitoring audio levels. Calls onSilence once continuous silence
     * of `duration` ms is detected — but only after speech has been heard first,
     * so it won't fire immediately if the mic opens to silence.
     */
    startSilenceDetection(onSilence, threshold = 0.01, duration = 1500) {
        if (!this.audioStream || !this.isRecording)
            return;
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx)
            return;
        if (!this.audioContext)
            this.audioContext = new AudioCtx();
        const analyser = this.audioContext.createAnalyser();
        analyser.fftSize = 512;
        this.analyserSource = this.audioContext.createMediaStreamSource(this.audioStream);
        this.analyserSource.connect(analyser);
        const buffer = new Float32Array(analyser.fftSize);
        let speechDetected = false;
        let silenceSince = null;
        const check = () => {
            if (!this.isRecording)
                return;
            analyser.getFloatTimeDomainData(buffer);
            const rms = Math.sqrt(buffer.reduce((s, v) => s + v * v, 0) / buffer.length);
            if (rms >= threshold) {
                speechDetected = true;
                silenceSince = null;
            }
            else if (speechDetected) {
                if (!silenceSince)
                    silenceSince = Date.now();
                else if (Date.now() - silenceSince >= duration) {
                    this.stopSilenceDetection();
                    onSilence();
                    return;
                }
            }
            this.silenceFrame = requestAnimationFrame(check);
        };
        this.silenceFrame = requestAnimationFrame(check);
    }
    stopSilenceDetection() {
        if (this.silenceFrame !== null) {
            cancelAnimationFrame(this.silenceFrame);
            this.silenceFrame = null;
        }
        if (this.analyserSource) {
            this.analyserSource.disconnect();
            this.analyserSource = null;
        }
    }
    stopRecording() {
        return new Promise((resolve) => {
            if (!this.recorder || !this.isRecording) {
                resolve(null);
                return;
            }
            this.stopSilenceDetection();
            this.recorder.onstop = () => {
                const audioBlob = new Blob(this.audioChunks, {
                    type: this.recorder?.mimeType || 'audio/webm'
                });
                this.isRecording = false;
                console.log('[WebRecorder] Recording stopped');
                resolve(audioBlob);
            };
            this.recorder.stop();
        });
    }
    getRecorderOptions() {
        // Prioritize formats supported by iOS
        const formats = this.environment.isIOS
            ? [
                'audio/mp4',
                'audio/wav',
                'audio/webm;codecs=opus',
                'audio/webm'
            ]
            : [
                'audio/webm;codecs=opus',
                'audio/webm',
                'audio/ogg;codecs=opus',
                'audio/mp4',
                'audio/wav'
            ];
        for (const format of formats) {
            if (MediaRecorder.isTypeSupported(format)) {
                console.log(`[WebRecorder] Selected format: ${format}`);
                return { mimeType: format };
            }
        }
        console.warn('[WebRecorder] No supported format found - using default');
        return {};
    }
    getIsRecording() {
        return this.isRecording;
    }
    dispose() {
        this.stopSilenceDetection();
        if (this.recorder && this.isRecording) {
            this.recorder.stop();
        }
        this.recorder = null;
        if (this.audioStream) {
            this.audioStream.getTracks().forEach(track => track.stop());
            this.audioStream = null;
        }
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        this.isRecording = false;
        this.audioChunks = [];
        console.log('[WebRecorder] Disposed');
    }
}

/**
 * NativeRecorder - Native bridge based audio recording
 * Uses native iOS/Android APIs through bridge communication
 */
class NativeRecorder {
    constructor(nativeBridge) {
        this.isRecording = false;
        this.nativeBridge = nativeBridge;
    }
    async initialize() {
        try {
            const result = await this.nativeBridge.initialize();
            if (!result.success) {
                throw new Error(result.error || 'Native initialization failed');
            }
            console.log('[NativeRecorder] Initialized successfully');
            return { success: true };
        }
        catch (err) {
            console.error('[NativeRecorder] Initialization failed:', err);
            return {
                success: false,
                error: err instanceof Error ? err.message : 'Unknown error'
            };
        }
    }
    async startRecording(onDataAvailable) {
        if (this.isRecording) {
            console.warn('[NativeRecorder] Already recording');
            return;
        }
        try {
            const result = await this.nativeBridge.startRecording();
            if (!result.success) {
                throw new Error(result.error || 'Native recording failed');
            }
            this.isRecording = true;
            console.log('[NativeRecorder] Recording started');
            if (onDataAvailable) {
                this.nativeBridge.onDataAvailable = onDataAvailable;
            }
        }
        catch (err) {
            console.error('[NativeRecorder] Start recording failed:', err);
            throw err;
        }
    }
    async stopRecording() {
        if (!this.isRecording) {
            return null;
        }
        try {
            const result = await this.nativeBridge.stopRecording();
            if (!result.success) {
                throw new Error(result.error || 'Failed to stop recording');
            }
            this.isRecording = false;
            console.log('[NativeRecorder] Recording stopped');
            // Convert Base64 or URL data to Blob
            const audioBlob = await this.convertNativeAudioToBlob(result.audioData);
            return audioBlob;
        }
        catch (err) {
            console.error('[NativeRecorder] Stop recording failed:', err);
            throw err;
        }
    }
    async convertNativeAudioToBlob(audioData) {
        if (!audioData) {
            return null;
        }
        // Convert Base64 string to Blob
        if (typeof audioData === 'string') {
            const binaryString = atob(audioData);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            return new Blob([bytes], { type: 'audio/wav' });
        }
        return null;
    }
    getIsRecording() {
        return this.isRecording;
    }
    dispose() {
        this.nativeBridge.dispose();
        this.isRecording = false;
        console.log('[NativeRecorder] Disposed');
    }
}

/**
 * VoiceInputManager - Unified voice input management
 * Detects WebView environment and uses Web API or Native Bridge for audio recording
 */
class VoiceInputManager {
    constructor() {
        this.useNativeBridge = false;
        this.webRecorder = null;
        this.nativeRecorder = null;
        this.nativeBridge = null;
        this._environment = PlatformDetector.detect();
    }
    get environment() {
        return this._environment;
    }
    async initialize() {
        console.log('[VoiceInputManager] Environment:', this._environment);
        // iOS WebView prioritizes Native Bridge
        if (this._environment.isIOSWebView) {
            console.log('[VoiceInputManager] iOS WebView detected - using Native Bridge');
            return await this.initializeWithNativeBridge();
        }
        // Android WebView tries Web API first, falls back to Native Bridge
        if (this._environment.isAndroidWebView) {
            console.log('[VoiceInputManager] Android WebView detected - trying Web API');
            const webResult = await this.initializeWithWebAPI();
            if (!webResult.success) {
                console.log('[VoiceInputManager] Web API failed - switching to Native Bridge');
                return await this.initializeWithNativeBridge();
            }
            return webResult;
        }
        // Regular web browser
        return await this.initializeWithWebAPI();
    }
    async initializeWithWebAPI() {
        this.webRecorder = new WebRecorder(this._environment);
        const result = await this.webRecorder.initialize();
        if (result.success) {
            this.useNativeBridge = false;
            console.log('[VoiceInputManager] Web API initialized');
            return { success: true, method: 'WebAPI' };
        }
        return {
            success: false,
            error: result.error,
            method: 'WebAPI'
        };
    }
    async initializeWithNativeBridge() {
        try {
            this.nativeBridge = this.createNativeBridge();
            if (!this.nativeBridge) {
                throw new Error('Native Bridge not available');
            }
            this.nativeRecorder = new NativeRecorder(this.nativeBridge);
            const result = await this.nativeRecorder.initialize();
            if (!result.success) {
                throw new Error(result.error || 'Native initialization failed');
            }
            this.useNativeBridge = true;
            console.log('[VoiceInputManager] Native Bridge initialized');
            return { success: true, method: 'NativeBridge' };
        }
        catch (err) {
            console.error('[VoiceInputManager] Native Bridge initialization failed:', err);
            return {
                success: false,
                error: err instanceof Error ? err.message : 'Unknown error',
                method: 'NativeBridge'
            };
        }
    }
    createNativeBridge() {
        if (this._environment.isIOS) {
            return new IOSBridge();
        }
        else if (this._environment.isAndroid) {
            return new AndroidBridge();
        }
        return null;
    }
    async startRecording(onDataAvailable) {
        if (this.isRecording()) {
            console.warn('[VoiceInputManager] Already recording');
            return;
        }
        if (this.useNativeBridge && this.nativeRecorder) {
            await this.nativeRecorder.startRecording(onDataAvailable);
        }
        else if (this.webRecorder) {
            await this.webRecorder.startRecording(onDataAvailable);
        }
    }
    async stopRecording() {
        if (!this.isRecording()) {
            return null;
        }
        if (this.useNativeBridge && this.nativeRecorder) {
            return await this.nativeRecorder.stopRecording();
        }
        else if (this.webRecorder) {
            return await this.webRecorder.stopRecording();
        }
        return null;
    }
    startSilenceDetection(onSilence, threshold, duration) {
        this.webRecorder?.startSilenceDetection(onSilence, threshold, duration);
    }
    stopSilenceDetection() {
        this.webRecorder?.stopSilenceDetection();
    }
    isRecording() {
        if (this.useNativeBridge && this.nativeRecorder) {
            return this.nativeRecorder.getIsRecording();
        }
        else if (this.webRecorder) {
            return this.webRecorder.getIsRecording();
        }
        return false;
    }
    dispose() {
        if (this.nativeRecorder) {
            this.nativeRecorder.dispose();
            this.nativeRecorder = null;
        }
        if (this.webRecorder) {
            this.webRecorder.dispose();
            this.webRecorder = null;
        }
        this.nativeBridge = null;
        this.useNativeBridge = false;
        console.log('[VoiceInputManager] Disposed');
    }
}

export { APIClient, AndroidBridge, IOSBridge, NativeRecorder, PlatformDetector, VoiceInputManager, WebRecorder };
//# sourceMappingURL=index.esm.js.map
